import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { useTranslation } from '@/lib/contexts/I18nContext';
import { useCopilotContext, QUICK_ACTIONS } from './useCopilotContext';
import type { CopilotLang, CopilotStrings } from './copilot-translations';
import { copilotTranslations } from './copilot-translations';
import { useUIStore } from '@/lib/ui-store';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  navigationPath?: string;
}

// ─── Markdown-lite renderer (bold only) ──────────────────────────────────────
function renderMd(text: string) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? <strong key={i} className="font-semibold text-[rgb(var(--accent-primary))]">{p}</strong> : p
  );
}

// ─── Typing dots animation ────────────────────────────────────────────────────
const TypingDots = memo(() => (
  <span className="inline-flex gap-[3px] items-center h-4">
    {[0, 1, 2].map(i => (
      <span key={i} className="w-1.5 h-1.5 rounded-full bg-current opacity-60"
        style={{ animation: `copilot-dot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
    ))}
  </span>
));

// ─── Single message bubble ────────────────────────────────────────────────────
const MessageBubble = memo(({ msg, isRTL, navigatingLabel, goLabel, onNav }:
  { msg: Message; isRTL: boolean; navigatingLabel: string; goLabel: string; onNav: (p: string) => void }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-2 ${isUser ? (isRTL ? 'flex-row' : 'flex-row-reverse') : 'flex-row'}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[rgb(var(--accent-primary))] to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-white text-[10px] font-bold">AI</span>
        </div>
      )}
      <div className={`max-w-[82%] ${isUser ? 'ms-auto' : ''}`}>
        <div className={`rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
          isUser
            ? 'bg-[rgb(var(--accent-primary))] text-white rounded-tr-sm'
            : 'bg-[rgb(var(--bg-tertiary))] text-[rgb(var(--text-primary))] rounded-tl-sm border border-[rgb(var(--border-color))]'
        }`}>
          {renderMd(msg.content)}
        </div>
        {msg.navigationPath && (
          <button onClick={() => onNav(msg.navigationPath!)}
            className="mt-1.5 ms-1 text-[11px] text-[rgb(var(--accent-primary))] hover:underline font-medium flex items-center gap-1">
            <span>{navigatingLabel}</span>
            <span className="opacity-70">{goLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
});

// ─── Smart response engine ────────────────────────────────────────────────────
function generateResponse(
  input: string,
  _pathname: string,
  contextAnswer: string,
  _strings: CopilotStrings,
): { content: string; navigationPath?: string } {
  const q = input.toLowerCase().trim();

  // Navigation intent patterns (FR + EN + AR keywords)
  const navMap: Array<[string[], string, string]> = [
    [['dashboard','tableau de bord','لوحة التحكم'], '/dashboard', ''],
    [['créer','create','company','entreprise','إنشاء شركة'], '/dashboard/companies/creer-entreprise', ''],
    [['document','statut','wathiqa','وثيقة'], '/dashboard/companies/documents', ''],
    [['finance','comptab','مالية','محاسبة'], '/dashboard/finance', ''],
    [['kpi','indicateur','indicator','مؤشر'], '/dashboard/finance/indicateurs', ''],
    [['trésor','treasury','خزينة'], '/dashboard/finance/tresorerie', ''],
    [['advisor','conseiller','مستشار'], '/dashboard/finance/advisor', ''],
    [['partenaire','partner','شريك'], '/dashboard/finance/partenaires', ''],
    [['genvest','invest','portfolio','محفظة'], '/dashboard/genvest', ''],
    [['opportunit','فرص'], '/dashboard/genvest/opportunites', ''],
    [['risque','risk','مخاطر'], '/dashboard/genvest/simulation-risques', ''],
    [['infrastructure','database','base de données','قاعدة'], '/dashboard/infrastructure', ''],
    [['sécurit','security','أمن'], '/dashboard/infrastructure/security', ''],
    [['setting','paramètre','إعداد'], '/dashboard/settings', ''],
    [['conformit','compliance','امتثال'], '/dashboard/companies/conformite', ''],
    [['validation','marque','brand','علامة'], '/dashboard/companies/validation-marque', ''],
    [['gestion','manage','إدارة'], '/dashboard/companies/gestion', ''],
  ];

  for (const [keywords, path, _] of navMap) {
    if (keywords.some(k => q.includes(k))) {
      return { content: contextAnswer, navigationPath: path };
    }
  }

  // "where am I" / current page questions
  if (q.match(/où|where|ici|here|current|actuel|أين|هنا/)) {
    return { content: contextAnswer };
  }

  // help / generic
  if (q.match(/help|aide|comment|how|مساعدة|كيف/)) {
    return { content: contextAnswer };
  }

  // Default: describe current page
  return { content: contextAnswer };
}

// ─── Main Component ───────────────────────────────────────────────────────────
export const AICopilot = memo(() => {
  const { language, isRTL } = useTranslation();
  const lang = (language as CopilotLang) in copilotTranslations ? language as CopilotLang : 'fr';
  const strings = copilotTranslations[lang];
  const { pathname, contextAnswer, navigateTo } = useCopilotContext(lang);
  const { isSidebarOpen } = useUIStore();

  const [open, setOpen]       = useState(false);
  const [minimized, setMin]   = useState(false);
  const [messages, setMsgs]   = useState<Message[]>([]);
  const [input, setInput]     = useState('');
  const [typing, setTyping]   = useState(false);
  const [pulse, setPulse]     = useState(true);
  const endRef                = useRef<HTMLDivElement>(null);
  const inputRef              = useRef<HTMLTextAreaElement>(null);

  // Pulse launcher dot every 8s
  useEffect(() => {
    const t = setInterval(() => setPulse(p => !p), 8000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, typing]);

  // Focus input when opened
  useEffect(() => { if (open && !minimized) inputRef.current?.focus(); }, [open, minimized]);

  // Inject welcome message on first open
  const handleOpen = useCallback(() => {
    setOpen(true);
    setMin(false);
    if (messages.length === 0) {
      setMsgs([{
        id: 'welcome',
        role: 'assistant',
        content: `${strings.welcomeTitle}\n\n${strings.welcomeSubtitle}`,
        timestamp: new Date(),
      }]);
    }
  }, [messages.length, strings]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || typing) return;
    setInput('');

    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: text, timestamp: new Date() };
    setMsgs(prev => [...prev, userMsg]);
    setTyping(true);

    // Simulate AI thinking delay (300-900ms)
    await new Promise(r => setTimeout(r, 300 + Math.random() * 600));

    const { content, navigationPath } = generateResponse(text, pathname, contextAnswer, strings);
    const aiMsg: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content,
      timestamp: new Date(),
      navigationPath,
    };
    setMsgs(prev => [...prev, aiMsg]);
    setTyping(false);
  }, [input, typing, pathname, contextAnswer, strings]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleQuickAction = useCallback(async (key: string, path: string) => {
    const s = strings.actions as any;
    const label = s[key] ?? key;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: label, timestamp: new Date() };
    setMsgs(prev => [...prev, userMsg]);
    setTyping(true);
    await new Promise(r => setTimeout(r, 350));
    setMsgs(prev => [...prev, {
      id: (Date.now()+1).toString(),
      role: 'assistant',
      content: contextAnswer,
      timestamp: new Date(),
      navigationPath: path,
    }]);
    setTyping(false);
  }, [strings, contextAnswer]);

  const handleNav = useCallback((path: string) => {
    navigateTo(path);
    setMin(true);
  }, [navigateTo]);

  const handleClear = () => setMsgs([]);

  // ── Panel dimensions ────────────────────────────────────────────────────────
  const panelW = 360;
  const panelH = 520;

  return (
    <>
      {/* ── CSS keyframes injected once ─────────────────────────────────── */}
      <style>{`
        @keyframes copilot-dot {
          0%,80%,100%{transform:scale(0.6);opacity:.4}
          40%{transform:scale(1);opacity:1}
        }
        @keyframes copilot-in {
          from{opacity:0;transform:translateY(12px) scale(.97)}
          to{opacity:1;transform:translateY(0) scale(1)}
        }
        @keyframes copilot-fab-in {
          from{opacity:0;transform:scale(.5)}
          to{opacity:1;transform:scale(1)}
        }
        .copilot-panel{animation:copilot-in .22s cubic-bezier(.22,1,.36,1)}
        .copilot-fab{animation:copilot-fab-in .35s cubic-bezier(.34,1.56,.64,1)}
      `}</style>

      {/* ── Floating Action Button ────────────────────────────────────────── */}
      {!open && !isSidebarOpen && (
        <button
          id="copilot-fab"
          aria-label={strings.buttonLabel}
          onClick={handleOpen}
          className="copilot-fab fixed bottom-6 right-6 z-[9999] w-12 h-12 sm:w-14 sm:h-14 rounded-full
            bg-gradient-to-br from-blue-500 via-[rgb(var(--accent-primary))] to-blue-800
            flex items-center justify-center
            border-2 border-white/20
            shadow-[0_4px_24px_rgba(37,99,235,.55),0_1px_4px_rgba(0,0,0,.25)]
            hover:shadow-[0_8px_36px_rgba(37,99,235,.75),0_2px_8px_rgba(0,0,0,.3)]
            hover:scale-110 active:scale-95
            transition-all duration-200 ease-out
            focus:outline-none focus:ring-4 focus:ring-blue-400/40"
        >
          {/* Chat icon — always white for max contrast */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            <line x1="9" y1="10" x2="15" y2="10" strokeWidth="1.8"/>
            <line x1="9" y1="13" x2="13" y2="13" strokeWidth="1.8"/>
          </svg>
          {/* Online pulse dot */}
          <span className={`absolute top-0.5 right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-emerald-400
            border-2 border-white ring-2 ring-emerald-400/30
            ${pulse ? 'scale-100' : 'scale-75'} transition-transform duration-700`} />
        </button>
      )}

      {/* ── Chat Panel ────────────────────────────────────────────────────── */}
      {open && (
        <div
          id="copilot-panel"
          dir={isRTL ? 'rtl' : 'ltr'}
          className={cn(
            "copilot-panel fixed z-[9999] transition-all duration-300",
            "bottom-0 right-0 w-full h-full sm:bottom-6 sm:right-6 sm:w-[360px] sm:h-[520px] sm:rounded-2xl",
            "bg-[rgb(var(--bg-primary))] border border-[rgb(var(--border-color))]",
            "shadow-[0_8px_48px_rgba(0,0,0,.22),0_2px_12px_rgba(0,0,0,.12)]",
            "flex flex-col overflow-hidden",
            minimized && "h-[56px] sm:h-[56px]"
          )}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[rgb(var(--border-color))]
            bg-gradient-to-r from-[rgb(var(--accent-primary))]/8 to-transparent flex-shrink-0">
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[rgb(var(--accent-primary))] to-blue-700
              flex items-center justify-center flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a4 4 0 0 1 4 4 4 4 0 0 1-4 4 4 4 0 0 1-4-4 4 4 0 0 1 4-4"/>
                <path d="M12 10c-5 0-8 2.5-8 5v1h16v-1c0-2.5-3-5-8-5"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-[rgb(var(--text-primary))] truncate leading-none">{strings.title}</p>
              <p className="text-[10px] text-[rgb(var(--text-muted))] mt-0.5 truncate leading-none">{strings.subtitle}</p>
            </div>
            {/* Header actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {!minimized && (
                <button onClick={handleClear} title={strings.clearLabel}
                  className="w-6 h-6 flex items-center justify-center rounded-lg text-[rgb(var(--text-muted))]
                    hover:text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--bg-tertiary))] transition-colors">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                    <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
                  </svg>
                </button>
              )}
              <button onClick={() => setMin(m => !m)} title={strings.minimizeLabel}
                className="w-6 h-6 flex items-center justify-center rounded-lg text-[rgb(var(--text-muted))]
                  hover:text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--bg-tertiary))] transition-colors">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round">
                  {minimized
                    ? <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>
                    : <line x1="5" y1="12" x2="19" y2="12"/>}
                </svg>
              </button>
              <button onClick={() => setOpen(false)} title={strings.closeLabel}
                className="w-6 h-6 flex items-center justify-center rounded-lg text-[rgb(var(--text-muted))]
                  hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ── Body (hidden when minimized) ───────────────────────────── */}
          {!minimized && (
            <>
              {/* Messages area */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 custom-scrollbar">
                {messages.map(msg => (
                  <MessageBubble key={msg.id} msg={msg} isRTL={isRTL}
                    navigatingLabel={strings.navigatingTo} goLabel={strings.goButton}
                    onNav={handleNav} />
                ))}
                {typing && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[rgb(var(--accent-primary))] to-blue-600
                      flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-[10px] font-bold">AI</span>
                    </div>
                    <div className="rounded-2xl rounded-tl-sm px-3 py-2.5 bg-[rgb(var(--bg-tertiary))]
                      border border-[rgb(var(--border-color))] text-[rgb(var(--text-muted))] text-[13px]">
                      <span className="me-1.5 text-[11px]">{strings.typingLabel.split('…')[0]}</span>
                      <TypingDots />
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* Quick actions */}
              <div className="px-3 py-2 border-t border-[rgb(var(--border-color))] bg-[rgb(var(--bg-secondary))]/50">
                <p className="text-[10px] font-semibold text-[rgb(var(--text-muted))] uppercase ltr:tracking-wider rtl:tracking-normal mb-1.5">
                  {strings.quickActionsTitle}
                </p>
                <div className="flex flex-wrap gap-1">
                  {QUICK_ACTIONS.map(({ key, path, icon }) => (
                    <button key={key} onClick={() => handleQuickAction(key, path)}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium
                        bg-[rgb(var(--bg-tertiary))] hover:bg-[rgb(var(--accent-primary))]/10
                        text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--accent-primary))]
                        border border-[rgb(var(--border-color))] hover:border-[rgb(var(--accent-primary))]/30
                        transition-all duration-150 active:scale-95">
                      <span>{icon}</span>
                      <span>{(strings.actions as any)[key]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Input area */}
              <div className="px-3 py-2.5 border-t border-[rgb(var(--border-color))] flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  id="copilot-input"
                  rows={1}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={strings.placeholder}
                  className="flex-1 resize-none rounded-xl px-3 py-2 text-[13px]
                    bg-[rgb(var(--bg-tertiary))] border border-[rgb(var(--border-color))]
                    text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))]
                    focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent-primary))]/20
                    focus:border-[rgb(var(--accent-primary))] transition-all duration-200
                    min-h-[36px] max-h-[80px] custom-scrollbar"
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || typing}
                  aria-label={strings.sendLabel}
                  className="w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
                    bg-[rgb(var(--accent-primary))] text-white
                    disabled:opacity-40 disabled:cursor-not-allowed
                    hover:bg-[rgb(var(--accent-hover))] active:scale-95
                    transition-all duration-150 shadow-sm"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    className={isRTL ? 'rotate-180' : ''}>
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
});

AICopilot.displayName = 'AICopilot';
export default AICopilot;
